"use client"

import React, { useState, useEffect, useCallback, useMemo } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import actionTypes from "../../../constants/actionTypes"
import "../../../resources/scss/info-input.scss"
import { Mobile, PC, Tablet, Notebook } from "../../ReactiveMode"

// 약관 모달 컴포넌트 (React.memo로 최적화)
const TermsModal = React.memo(({ show, onClose, title, content }) => {
    const handleOverlayClick = useCallback(
        (e) => {
            if (e.target === e.currentTarget) {
                onClose()
            }
        },
        [onClose],
    )

    const handleContentClick = useCallback((e) => {
        e.stopPropagation()
    }, [])

    if (!show) return null

    return (
        <div className="modal-overlay" onClick={handleOverlayClick}>
            <div className="modal-content" onClick={handleContentClick}>
                <div className="modal-header">
                    <h3>{title}</h3>
                    <button className="close-button" onClick={onClose} type="button">
                        ×
                    </button>
                </div>
                <div className="modal-body">
                    <pre>{content}</pre>
                </div>
                <div className="modal-footer">
                    <button className="confirm-button" onClick={onClose} type="button">
                        확인
                    </button>
                </div>
            </div>
        </div>
    )
})

// 성공 메시지 컴포넌트 (React.memo로 최적화)
const SuccessMessage = React.memo(() => (
    <div className="success-message">
        <div className="success-icon">✓</div>
        <h2>약관 동의가 완료되었습니다!</h2>
        <p>이제 모든 서비스를 이용하실 수 있습니다.</p>
        <p className="redirect-text">잠시 후 메인 페이지로 이동합니다...</p>
    </div>
))

// 사용자 정보 섹션 컴포넌트 (React.memo로 최적화)
const UserInfoSection = React.memo(({ userInfo, isLoadingUserInfo }) => (
    <div className="user-info-section">
        <h3>개인정보 확인</h3>
        <p className="info-description">아래 정보를 확인해주세요. 정보 수정이 필요한 경우 관리자에게 문의해주세요.</p>

        {isLoadingUserInfo ? (
            <div className="loading-info">
                <div className="loading-spinner"></div>
                <p>정보를 불러오는 중...</p>
            </div>
        ) : (
            <div className="user-info-grid">
                <div className="info-item">
                    <label>이름</label>
                    <input type="text" value={userInfo?.name || "정보 없음"} disabled className="disabled-input" />
                </div>
                <div className="info-item">
                    <label>연락처</label>
                    <input type="text" value={userInfo?.phone || "정보 없음"} disabled className="disabled-input" />
                </div>
                <div className="info-item">
                    <label>이메일</label>
                    <input type="text" value={userInfo?.email || "정보 없음"} disabled className="disabled-input" />
                </div>
            </div>
        )}
    </div>
))

const InfoInput = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    // Redux state
    const { isLogin, isInfo, id } = useSelector((state) => state.login)
    const { userInfo, isLoadingUserInfo, userInfoError, isSubmittingMember, memberError, memberSuccess } = useSelector(
        (state) => state.user,
    )

    const [formData, setFormData] = useState({
        termsAgreed: false,
        privacyAgreed: false,
    })

    const [errors, setErrors] = useState({})
    const [modalState, setModalState] = useState({
        showTerms: false,
        showPrivacy: false,
    })

    // 디버깅: Redux 상태 변화 추적
    useEffect(() => {
    }, [isLogin, isInfo, id, isSubmittingMember, memberSuccess, memberError])

    // 약관 내용을 useMemo로 메모이제이션
    const termsContent = useMemo(
        () => `
서비스 이용약관

제1조 (목적)
본 약관은 교회에서 제공하는 온라인 서비스(이하 "서비스")의 이용과 관련하여 교회와 이용자 간의 권리, 의무 및 책임사항, 기타 필요한 사항을 규정함을 목적으로 합니다.

제2조 (정의)
1. "서비스"란 교회에서 제공하는 모든 온라인 서비스를 의미합니다.
2. "이용자"란 본 약관에 따라 서비스를 이용하는 회원을 말합니다.
3. "회원"이란 교회와 서비스 이용계약을 체결하고 이용자 아이디(ID)를 부여받은 자를 의미합니다.

제3조 (약관의 효력 및 변경)
1. 본 약관은 서비스를 이용하고자 하는 모든 이용자에 대하여 그 효력을 발생합니다.
2. 교회는 필요한 경우 본 약관을 변경할 수 있으며, 변경된 약관은 공지사항을 통해 7일 전에 공지합니다.
3. 이용자가 변경된 약관에 동의하지 않는 경우, 서비스 이용을 중단하고 탈퇴할 수 있습니다.

제4조 (서비스의 제공)
교회는 다음과 같은 서비스를 제공합니다:
1. 교회 소식 및 공지사항 제공
2. 설교 영상 및 자료 제공
3. 온라인 커뮤니티 서비스
4. 교회 행사 및 프로그램 안내
5. 온라인 신청 및 등록 서비스
6. 기타 교회 관련 정보 서비스

제5조 (회원가입)
1. 회원가입은 이용자가 약관의 내용에 대하여 동의를 하고 회원가입신청을 한 후 교회가 이러한 신청에 대하여 승낙함으로써 체결됩니다.
2. 교회는 다음 각 호에 해당하는 신청에 대하여는 승낙을 하지 않을 수 있습니다:
   - 실명이 아니거나 타인의 명의를 이용한 경우
   - 허위의 정보를 기재하거나, 교회가 제시하는 내용을 기재하지 않은 경우

제6조 (회원의 의무)
1. 회원은 서비스 이용 시 관련 법령과 본 약관을 준수해야 합니다.
2. 회원은 타인의 권리를 침해하거나 불쾌감을 주는 행위를 해서는 안 됩니다.
3. 회원은 교회의 승인 없이 서비스를 이용하여 영업활동을 할 수 없습니다.

제7조 (서비스의 중단)
1. 교회는 컴퓨터 등 정보통신설비의 보수점검, 교체 및 고장, 통신의 두절 등의 사유가 발생한 경우에는 서비스의 제공을 일시적으로 중단할 수 있습니다.
2. 교회는 제1항의 사유로 서비스의 제공이 중단된 경우에 대하여 사전에 통지합니다.

제8조 (면책조항)
1. 교회는 천재지변 또는 이에 준하는 불가항력으로 인하여 서비스를 제공할 수 없는 경우에는 서비스 제공에 관한 책임이 면제됩니다.
2. 교회는 회원의 귀책사유로 인한 서비스 이용의 장애에 대하여는 책임을 지지 않습니다.

제9조 (분쟁해결)
본 약관에 관해 분쟁이 생길 경우, 교회 소재지 법원을 관할 법원으로 합니다.

부칙
본 약관은 2025년 7월 1일부터 적용됩니다.
  `,
        [],
    )

    const privacyContent = useMemo(
        () => `
개인정보처리방침

교회는 「개인정보 보호법」 제30조에 따라 정보주체의 개인정보를 보호하고 이와 관련한 고충을 신속하고 원활하게 처리할 수 있도록 하기 위하여 다음과 같이 개인정보 처리방침을 수립·공개합니다.

제1조 (개인정보의 처리목적)
교회는 다음의 목적을 위하여 개인정보를 처리합니다. 처리하고 있는 개인정보는 다음의 목적 이외의 용도로는 이용되지 않으며, 이용 목적이 변경되는 경우에는 「개인정보 보호법」 제18조에 따라 별도의 동의를 받는 등 필요한 조치를 이행할 예정입니다.

1. 회원 가입 및 관리
   - 회원 가입의사 확인, 회원제 서비스 제공에 따른 본인 식별·인증
   - 회원자격 유지·관리, 서비스 부정이용 방지, 각종 고지·통지 목적

2. 교회 서비스 제공
   - 교회 소식 및 공지사항 전달
   - 설교 및 교육 자료 제공
   - 교회 행사 및 프로그램 안내
   - 온라인 신청 및 등록 서비스 제공

3. 민원사무 처리
   - 민원인의 신원 확인, 민원사항 확인, 사실조사를 위한 연락·통지
   - 처리결과 통보

제2조 (개인정보의 처리 및 보유기간)
① 교회는 법령에 따른 개인정보 보유·이용기간 또는 정보주체로부터 개인정보를 수집 시에 동의받은 개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다합니다.

② 각각의 개인정보 처리 및 보유 기간은 다음과 같습니다:

1. 회원 가입 및 관리: 회원 탈퇴 시까지
   - 다만, 다음의 사유에 해당하는 경우에는 해당 기간 종료 시까지
   - 관계 법령 위반에 따른 수사·조사 등이 진행 중인 경우에는 해당 수사·조사 종료 시까지
   - 웹사이트 이용에 따른 채권·채무관계 잔존 시에는 해당 채권·채무관계 정산 시까지

2. 부정 이용 방지: 회원 탈퇴 후 1년

제3조 (개인정보의 제3자 제공)
① 교회는 개인정보를 제1조(개인정보의 처리목적)에서 명시한 범위 내에서만 처리하며, 정보주체의 동의, 법률의 특별한 규정 등 「개인정보 보호법」 제17조 및 제18조에 해당하는 경우에만 개인정보를 제3자에게 제공합니다.

② 교회는 다음과 같이 개인정보를 제3자에게 제공하고 있습니다:
현재 제3자 제공은 하지 않습니다.

제4조 (개인정보처리의 위탁)
① 교회는 원활한 개인정보 업무처리를 위하여 다음과 같이 개인정보 처리업무를 위탁하고 있습니다:

1. 웹사이트 운영 및 관리
   - 위탁받는 자(수탁자): 웹 개발업체
   - 위탁하는 업무의 내용: 웹사이트 시스템 운영 및 유지보수
   - 위탁기간: 계약 기간

② 교회는 위탁계약 체결 시 「개인정보 보호법」 제26조에 따라 위탁업무 수행목적 외 개인정보 처리금지, 기술적·관리적 보호조치, 재위탁 제한, 수탁자에 대한 관리·감독, 손해배상 등 책임에 관한 사항을 계약서 등 문서에 명시하고, 수탁자가 개인정보를 안전하게 처리하는지를 감독하고 있습니다.

제5조 (정보주체의 권리·의무 및 행사방법)
① 정보주체는 교회에 대해 언제든지 다음 각 호의 개인정보 보호 관련 권리를 행사할 수 있습니다:

1. 개인정보 처리현황 통지요구
2. 개인정보 열람요구
3. 개인정보 정정·삭제요구
4. 개인정보 처리정지요구

② 제1항에 따른 권리 행사는 교회에 대해 「개인정보 보호법」 시행령 제41조제1항에 따라 서면, 전자우편, 모사전송(FAX) 등을 통하여 하실 수 있으며 교회는 이에 대해 지체없이 조치하겠습니다.

제6조 (개인정보의 파기)
① 교회는 개인정보 보유기간의 경과, 처리목적 달성 등 개인정보가 불필요하게 되었을 때에는 지체없이 해당 개인정보를 파기합니다.

② 개인정보 파기의 절차 및 방법은 다음과 같습니다:
1. 파기절차: 불필요한 개인정보 및 개인정보파일은 개인정보 보호책임자의 승인을 받아 파기합니다.
2. 파기방법:
   - 전자적 파일 형태: 기록을 재생할 수 없는 기술적 방법을 사용하여 완전삭제
   - 종이 문서: 분쇄하거나 소각

제7조 (개인정보의 안전성 확보조치)
교회는 개인정보의 안전성 확보를 위해 다음과 같은 조치를 취하고 있습니다:

1. 관리적 조치: 내부관리계획 수립·시행, 정기적 직원 교육 등
2. 기술적 조치: 개인정보처리시스템 등의 접근권한 관리, 접근통제시스템 설치, 고유식별정보 등의 암호화, 보안프로그램 설치
3. 물리적 조치: 전산실, 자료보관실 등의 접근통제

제8조 (개인정보 보호책임자)
① 교회는 개인정보 처리에 관한 업무를 총괄해서 책임지고, 개인정보 처리와 관련한 정보주체의 불만처리 및 피해구제 등을 위하여 아래와 같이 개인정보 보호책임자를 지정하고 있습니다:

▶ 개인정보 보호책임자
성명: 김진영 목사
직책: 개인정보 보호책임자
연락처: 010-8904-6418, wlsdud6419@naver.com

② 정보주체께서는 교회의 서비스를 이용하시면서 발생한 모든 개인정보 보호 관련 문의, 불만처리, 피해구제 등에 관한 사항을 개인정보 보호책임자에게 문의하실 수 있습니다.

제9조 (권익침해 구제방법)
정보주체는 아래의 기관에 대해 개인정보 침해신고, 상담 등을 문의하실 수 있습니다:

▶ 개인정보 침해신고센터 (privacy.go.kr)
- 신고전화: 국번없이 182
- 주소: (01818) 서울특별시 종로구 세종대로 209 정부서울청사 4층

▶ 개인정보 분쟁조정위원회 (www.kopico.go.kr)
- 신고전화: 국번없이 1833-6972
- 주소: (03171) 서울특별시 종로구 세종대로 209 정부서울청사 4층

제10조 (개인정보 처리방침 변경)
① 이 개인정보처리방침은 시행일로부터 적용되며, 법령 및 방침에 따른 변경내용의 추가, 삭제 및 정정이 있는 경우에는 변경사항의 시행 7일 전부터 공지사항을 통하여 고지할 것입니다.

부칙
제1조 (시행일) 이 방침은 2025년 7월 1일부터 시행됩니다.
  `,
        [],
    )

    // 로그인 상태 및 정보 입력 상태 확인 (LOGIN_CHECK 제거)
    useEffect(() => {
        if (!isLogin) {
            navigate("/login")
            return
        }

        if (isInfo) {
            navigate("/")
            return
        }

        // LOGIN_CHECK 제거 - 불필요한 API 호출 방지
    }, [isLogin, isInfo, navigate])

    // 사용자 정보 가져오기 (조건을 더 엄격하게)
    useEffect(() => {
        // 로그인 상태이고, 정보가 입력되지 않았고, ID가 있을 때만 사용자 정보 가져오기
        if (isLogin && !isInfo && id && !userInfo.name) {
            dispatch({ type: actionTypes.USER_INFO_FETCH })
        }
    }, [isLogin, isInfo, id, dispatch, userInfo.name])

    // 회원 정보 입력 성공 시 처리 (디버깅 강화)
    useEffect(() => {
        if (memberSuccess) {

            // LOGIN_INFO_APPROVAL 액션도 함께 디스패치
            dispatch({ type: actionTypes.LOGIN_INFO_APPROVAL })

            // 페이지 이동
            navigate("/")
        }
    }, [memberSuccess, navigate, dispatch])

    // 입력 필드 변경 핸들러
    const handleChange = useCallback(
        (e) => {
            const { name, checked } = e.target

            setFormData((prev) => ({
                ...prev,
                [name]: checked,
            }))

            // 에러 상태 초기화
            if (errors[name]) {
                setErrors((prev) => ({
                    ...prev,
                    [name]: null,
                }))
            }
        },
        [errors],
    )

    // 폼 검증
    const validateForm = useCallback(() => {
        const newErrors = {}

        if (!formData.termsAgreed) {
            newErrors.termsAgreed = "이용약관에 동의해주세요"
        }

        if (!formData.privacyAgreed) {
            newErrors.privacyAgreed = "개인정보처리방침에 동의해주세요"
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }, [formData.termsAgreed, formData.privacyAgreed])

    // 폼 제출 핸들러 (여기서만 MEMBER_INPUT 호출)
    const handleSubmit = useCallback(
        (e) => {
            e.preventDefault()

            if (!validateForm()) {
                return
            }


            // 오직 폼 제출 시에만 MEMBER_INPUT 호출
            dispatch({
                type: actionTypes.USER_INFO_INPUT_FETCH,
                payload: {
                    id: Number(id),
                },
            })
        },
        [validateForm, dispatch, id],
    )

    // 모달 핸들러들 (상태를 하나로 통합)
    const handleShowTerms = useCallback(() => {
        setModalState((prev) => ({ ...prev, showTerms: true }))
    }, [])

    const handleCloseTerms = useCallback(() => {
        setModalState((prev) => ({ ...prev, showTerms: false }))
    }, [])

    const handleShowPrivacy = useCallback(() => {
        setModalState((prev) => ({ ...prev, showPrivacy: true }))
    }, [])

    const handleClosePrivacy = useCallback(() => {
        setModalState((prev) => ({ ...prev, showPrivacy: false }))
    }, [])

    // 전체 동의 핸들러
    const handleAllAgree = useCallback(() => {
        const allAgreed = formData.termsAgreed && formData.privacyAgreed
        setFormData({
            termsAgreed: !allAgreed,
            privacyAgreed: !allAgreed,
        })
        setErrors({})
    }, [formData.termsAgreed, formData.privacyAgreed])

    // 정보 입력 폼 컴포넌트
    const InfoForm = useMemo(
        () => (
            <form onSubmit={handleSubmit} className="info-form">
                <div className="form-header">
                    <img src={process.env.PUBLIC_URL + "/images/logo_b.png" || "/placeholder.svg"} alt="로고" className="logo" />
                    <h1>서비스 이용 약관</h1>
                    <p className="subtitle">개인정보를 확인하고 서비스 이용약관에 동의해주세요.</p>
                </div>

                {/* 개인정보 표시 섹션 */}
                <UserInfoSection userInfo={userInfo} isLoadingUserInfo={isLoadingUserInfo} />

                {/* 약관 동의 섹션 */}
                <div className="terms-section">
                    <div className="terms-intro">
                        <h3>약관 동의</h3>
                        <p>교회 온라인 서비스 이용을 위해 다음 약관에 동의해주세요.</p>
                    </div>

                    <div className="form-group checkbox-group">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                name="termsAgreed"
                                checked={formData.termsAgreed}
                                onChange={handleChange}
                                className={errors.termsAgreed ? "error" : ""}
                            />
                            <span className="checkmark"></span>
                            <span className="checkbox-text">
                <span className="required">[필수]</span> 이용약관에 동의합니다
                <button type="button" className="view-terms-button" onClick={handleShowTerms}>
                  약관 보기
                </button>
              </span>
                        </label>
                        {errors.termsAgreed && <div className="error-message">{errors.termsAgreed}</div>}
                    </div>

                    <div className="form-group checkbox-group">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                name="privacyAgreed"
                                checked={formData.privacyAgreed}
                                onChange={handleChange}
                                className={errors.privacyAgreed ? "error" : ""}
                            />
                            <span className="checkmark"></span>
                            <span className="checkbox-text">
                <span className="required">[필수]</span> 개인정보처리방침에 동의합니다
                <button type="button" className="view-terms-button" onClick={handleShowPrivacy}>
                  방침 보기
                </button>
              </span>
                        </label>
                        {errors.privacyAgreed && <div className="error-message">{errors.privacyAgreed}</div>}
                    </div>

                    <div className="all-agree-section">
                        <button type="button" className="all-agree-button" onClick={handleAllAgree}>
                            {formData.termsAgreed && formData.privacyAgreed ? "전체 동의 해제" : "전체 동의"}
                        </button>
                    </div>
                </div>

                {(memberError || userInfoError) && <div className="error-message global">{memberError || userInfoError}</div>}

                <button
                    type="submit"
                    className={`submit-button ${isSubmittingMember ? "submitting" : ""}`}
                    disabled={isSubmittingMember}
                >
                    {isSubmittingMember ? "처리 중..." : "동의하고 시작하기"}
                </button>

                {/* 약관 모달들 */}
                <TermsModal show={modalState.showTerms} onClose={handleCloseTerms} title="이용약관" content={termsContent} />
                <TermsModal
                    show={modalState.showPrivacy}
                    onClose={handleClosePrivacy}
                    title="개인정보처리방침"
                    content={privacyContent}
                />
            </form>
        ),
        [
            handleSubmit,
            userInfo,
            isLoadingUserInfo,
            formData,
            errors,
            handleChange,
            handleShowTerms,
            handleShowPrivacy,
            handleAllAgree,
            memberError,
            userInfoError,
            isSubmittingMember,
            modalState.showTerms,
            modalState.showPrivacy,
            handleCloseTerms,
            handleClosePrivacy,
            termsContent,
            privacyContent,
        ],
    )

    return (
        <section className="info-input-page">
            <PC>
                <div className="pc-container">{memberSuccess ? <SuccessMessage /> : InfoForm}</div>
            </PC>

            <Notebook>
                <div className="notebook-container">{memberSuccess ? <SuccessMessage /> : InfoForm}</div>
            </Notebook>

            <Tablet>
                <div className="tablet-container">{memberSuccess ? <SuccessMessage /> : InfoForm}</div>
            </Tablet>

            <Mobile>
                <div className="mobile-container">{memberSuccess ? <SuccessMessage /> : InfoForm}</div>
            </Mobile>
        </section>
    )
}

export default InfoInput
